import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useLocations } from '@/features/inventory/hooks';
import { useCreateEquipment, useEquipmentList } from '@/features/equipment/hooks';
import {
  EQUIPMENT_CATEGORIES,
  EQUIPMENT_CATEGORY_META,
  EQUIPMENT_STATUS_OPTIONS,
  suggestAssetTag,
  type EquipmentCategory,
  type EquipmentStatus,
} from '@/features/equipment/model';
import { inputDollarsToCents } from '@/lib/formatters';

const NO_LOCATION = '__none';
const PURCHASE_YEARS = { past: 40, future: 1 };

interface NewEquipmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCategory?: EquipmentCategory;
}

export function NewEquipmentDialog({ open, onOpenChange, defaultCategory }: NewEquipmentDialogProps) {
  const navigate = useNavigate();
  const create = useCreateEquipment();
  const { data: existing } = useEquipmentList();
  const { data: locations } = useLocations();

  const [category, setCategory] = useState<EquipmentCategory>(defaultCategory ?? 'shop');
  const [assetTag, setAssetTag] = useState('');
  const [tagEdited, setTagEdited] = useState(false);
  const [name, setName] = useState('');
  const [equipmentType, setEquipmentType] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [serial, setSerial] = useState('');
  const [status, setStatus] = useState<EquipmentStatus>('in_service');
  const [locationId, setLocationId] = useState(NO_LOCATION);
  const [acquiredAt, setAcquiredAt] = useState('');
  const [price, setPrice] = useState('');

  const existingTags = useMemo(() => (existing ?? []).map((e) => e.asset_tag), [existing]);

  useEffect(() => {
    if (!open) return;
    setCategory(defaultCategory ?? 'shop');
    setTagEdited(false);
    setName('');
    setEquipmentType('');
    setManufacturer('');
    setModel('');
    setSerial('');
    setStatus('in_service');
    setLocationId(NO_LOCATION);
    setAcquiredAt('');
    setPrice('');
  }, [open, defaultCategory]);

  useEffect(() => {
    if (!open || tagEdited) return;
    setAssetTag(suggestAssetTag(category, existingTags));
  }, [open, category, existingTags, tagEdited]);

  const typeOptions = useMemo(() => {
    const seen = new Set(EQUIPMENT_CATEGORY_META[category].typeSuggestions);
    for (const e of existing ?? []) {
      if (e.category === category && e.equipment_type) seen.add(e.equipment_type);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [category, existing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let priceCents: number | null;
    try {
      priceCents = inputDollarsToCents(price);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid purchase price.');
      return;
    }
    try {
      const row = await create.mutateAsync({
        asset_tag: assetTag,
        name,
        category,
        equipment_type: equipmentType,
        manufacturer,
        model,
        serial_number: serial,
        status,
        location_id: locationId === NO_LOCATION ? null : locationId,
        acquired_at: acquiredAt || null,
        purchase_price_cents: priceCents,
      });
      toast.success(`Added ${row.asset_tag}`);
      onOpenChange(false);
      navigate(`/catalog/equipment/${row.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add equipment.');
    }
  };

  const submitting = create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add equipment</DialogTitle>
            <DialogDescription>
              Purchase details, depreciation, maintenance and documents can be filled in on the
              equipment page afterwards.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="equipment-category">Category</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as EquipmentCategory)}>
                <SelectTrigger id="equipment-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {EQUIPMENT_CATEGORY_META[c].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="equipment-tag">
                Asset tag <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Input
                id="equipment-tag"
                value={assetTag}
                onChange={(e) => {
                  setAssetTag(e.target.value);
                  setTagEdited(true);
                }}
                maxLength={64}
                className="font-mono text-xs"
                required
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="equipment-name">
                Name <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Input
                id="equipment-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  category === 'injection'
                    ? 'e.g. Engel Victory 110'
                    : category === 'office'
                      ? 'e.g. Design laptop — Sam'
                      : category === 'prototype'
                        ? 'e.g. Prusa MK4 #2'
                        : 'e.g. Haas VF-2 mill'
                }
                maxLength={200}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="equipment-type">Type</Label>
              <Input
                id="equipment-type"
                value={equipmentType}
                onChange={(e) => setEquipmentType(e.target.value)}
                list="equipment-type-options"
                maxLength={120}
              />
              <datalist id="equipment-type-options">
                {typeOptions.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="equipment-status">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as EquipmentStatus)}>
                <SelectTrigger id="equipment-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="equipment-manufacturer">Manufacturer</Label>
              <Input
                id="equipment-manufacturer"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                maxLength={120}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="equipment-model">Model</Label>
              <Input
                id="equipment-model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                maxLength={120}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="equipment-serial">Serial number</Label>
              <Input
                id="equipment-serial"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                maxLength={120}
                className="font-mono text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="equipment-location">Location</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger id="equipment-location">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_LOCATION}>No location</SelectItem>
                  {(locations ?? []).map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="equipment-acquired">Purchase date</Label>
              <DatePicker
                id="equipment-acquired"
                value={acquiredAt}
                onChange={setAcquiredAt}
                placeholder="Pick a date"
                aria-label="Purchase date"
                showShortcuts={false}
                yearRange={PURCHASE_YEARS}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="equipment-price">Purchase price</Label>
              <Input
                id="equipment-price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                className="tabular-nums"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add equipment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
